// src/utils/dancerValidation.js
import { getDancersList } from './fieldCompatibility';

/**
 * Checks if a dancer is allocated to any event
 * @param {Object} dancer - The dancer object
 * @param {Array} events - Array of events to check against
 * @returns {boolean} - True if dancer is allocated to an event, false otherwise
 */
export const isDancerAllocatedToEvent = (dancer, events = []) => {
  if (!dancer || !Array.isArray(events)) return false;

  return events.some(event => {
    if (!event) return false;

    // Use getDancersList to properly handle both JSON strings and arrays
    const dancers = getDancersList(event);
    if (!Array.isArray(dancers)) return false;

    // Check if dancer is allocated by matching cloth numbers (support both naming conventions)
    const dancerClothNumber = dancer.clothNumber || dancer.cloth_number;
    return dancers.some(allocatedDancer => {
      const allocatedClothNumber = allocatedDancer.clothNumber || allocatedDancer.cloth_number;
      return allocatedClothNumber === dancerClothNumber;
    });
  });
};

/**
 * Checks if a dancer is checked in (available for operations)
 * @param {Object} dancer - The dancer object
 * @returns {boolean} - True if dancer is checked in, false otherwise
 */
export const isDancerCheckedIn = (dancer) => {
  if (!dancer) return false;
  // API ALWAYS returns camelCase field names
  return Boolean(dancer.registrationCheckin);
};

/**
 * Checks if a dancer is checked in for Marshall operations
 * @param {Object} dancer - The dancer object
 * @returns {boolean} - True if dancer is Marshall checked in, false otherwise
 */
export const isDancerMarshallCheckedIn = (dancer) => {
  if (!dancer) return false;
  // API ALWAYS returns camelCase field names
  return Boolean(dancer.marshallCheckin);
};

/**
 * Filters an array of dancers to only include checked-in dancers
 * @param {Array} dancers - Array of dancer objects
 * @returns {Array} - Array of checked-in dancers
 */
export const getCheckedInDancers = (dancers) => {
  if (!Array.isArray(dancers)) return [];
  return dancers.filter(isDancerCheckedIn);
};

/**
 * Gets a validation message for dancers that are not checked in
 * @param {Object} dancer - The dancer object
 * @param {Array} events - Array of events to check allocation against
 * @returns {string} - Validation message explaining why dancer cannot be used
 */
export const getDancerValidationMessage = (dancer, events = []) => {
  if (!dancer) return 'Dancer not found';

  const isAllocated = isDancerAllocatedToEvent(dancer, events);
  // ALWAYS use camelCase field names
  const hasRegistration = Boolean(dancer.registrationCheckin);

  if (!isAllocated) {
    return 'Dancer must be allocated to an event first before checking in';
  }

  if (!hasRegistration) {
    return 'Dancer must be checked in for Registration after allocation';
  }

  return 'Dancer is allocated and checked in - ready for use';
};

/**
 * Checks if a dancer can be used in other parts of the app
 * @param {Object} dancer - The dancer object
 * @param {Array} events - Array of events to check allocation against
 * @returns {Object} - Object with isValid boolean and message string
 */
export const validateDancerForAppUse = (dancer, events = []) => {
  const isAllocated = isDancerAllocatedToEvent(dancer, events);
  const isCheckedIn = isDancerCheckedIn(dancer);
  const isValid = isAllocated && isCheckedIn;
  const message = isValid ? 'Dancer is eligible for use (allocated and checked in)' : getDancerValidationMessage(dancer, events);

  return {
    isValid,
    message
  };
};

/**
 * Checks if a dancer can be checked in (must be allocated first)
 * @param {Object} dancer - The dancer object
 * @param {Array} events - Array of events to check allocation against
 * @returns {Object} - Object with canCheckIn boolean and message string
 */
export const canDancerCheckIn = (dancer, events = []) => {
  const isAllocated = isDancerAllocatedToEvent(dancer, events);
  const canCheckIn = isAllocated;
  const message = isAllocated ?
    'Dancer is allocated - can check in' :
    'Dancer must be allocated to an event first before checking in';

  return {
    canCheckIn,
    message
  };
};